import { Box, Flex, InputGroup, NumberInput, NumberInputField, Text, Tooltip } from "@chakra-ui/react";
import { Typography } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { ConditionFieldErrors } from "../../../../utils/validateConditionData";
import FieldError from "../FieldError";

interface DaySelectorProps {
    conditionData: any;
    updateConditionData: (val: { value?: string; fromValue?: string; toValue?: string }) => void;
    showTooltip?: boolean;
    tooltipText?: string;
    allowDecimals?: boolean;
    label?: string;
    validationMessage?: string;
    fieldErrors?: ConditionFieldErrors;
}

const DaySelector = ({
    conditionData,
    updateConditionData,
    showTooltip = false,
    tooltipText,
    allowDecimals = false,
    label,
    validationMessage,
    fieldErrors,
}: DaySelectorProps) => {
    const { t } = useTranslation();

    const displayLabel = label || t("Value");
    const [touched, setTouched] = useState({
        value: false,
        fromValue: false,
        toValue: false,
    });

    const handleBlur = (field: "value" | "fromValue" | "toValue") => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const validationErrors = useMemo(() => {
        const errors = {
            fromValue: "",
            toValue: "",
            value: "",
        };

        if (fieldErrors?.fromValue) {
            errors.fromValue = fieldErrors.fromValue;
        }

        if (fieldErrors?.toValue) {
            errors.toValue = fieldErrors.toValue;
        }

        if (fieldErrors?.value) {
            errors.value = fieldErrors.value;
        }

        if (conditionData?.condition === "between" || conditionData?.condition === "notBetween") {
            if (conditionData?.fromValue && Number(conditionData?.fromValue) < 0) {
                errors.fromValue = t("Negative numbers are not valid inputs");
            } else if (!allowDecimals && conditionData?.fromValue?.toString()?.includes(".")) {
                errors.fromValue = t("Only whole numbers are valid inputs");
            }

            if (conditionData?.toValue && Number(conditionData?.toValue) < 0) {
                errors.toValue = t("Negative numbers are not valid inputs");
            } else if (!allowDecimals && conditionData?.toValue?.toString()?.includes(".")) {
                errors.toValue = t("Only whole numbers are valid inputs");
            } else if (
                conditionData?.fromValue &&
                conditionData?.toValue &&
                Number(conditionData?.toValue) < Number(conditionData?.fromValue)
            ) {
                errors.toValue = validationMessage || t("To value must be greater than or equal to from value");
            }
        } else {
            if (conditionData?.value && Number(conditionData?.value) < 0) {
                errors.value = t("Negative numbers are not valid inputs");
            } else if (!allowDecimals && conditionData?.value?.toString()?.includes(".")) {
                errors.value = t("Only whole numbers are valid inputs");
            }
        }

        return errors;
    }, [conditionData, t, allowDecimals, fieldErrors, validationMessage]);

    const renderInputField = (
        field: "value" | "fromValue" | "toValue",
        value: string | undefined,
        onChange: (val: string) => void,
        error: string,
        withTooltip: boolean = true
    ) => {
        // Extract only the numeric part from the stored value (format: "value_operator")
        const displayValue = value?.includes('_') ? value.split('_')[0] : value;
        const displayOperator = value?.includes('_') ? value.split('_')[1] : conditionData?.operator;

        const handleChange = (val: string) => {
            onChange(`${val}_${conditionData?.operator}`);
        };

        if (displayOperator !== conditionData?.operator) {
            handleChange(displayValue?.toString() || '');
        }

        const showError = touched[field] && !!error;

        const inputElement = (
            <InputGroup>
                <NumberInput
                    width={"100%"}
                    value={displayValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(field)}
                    min={0}
                    precision={allowDecimals ? 2 : 0}
                    isInvalid={showError}
                >
                    <NumberInputField color={"#1A202C"} _invalid={{ borderColor: "red.500" }} />
                </NumberInput>
            </InputGroup>
        );

        if (showTooltip && withTooltip) {
            return (
                <Tooltip label={tooltipText} fontSize="xs" placement="top" hasArrow>
                    {inputElement}
                </Tooltip>
            );
        }

        return inputElement;
    };

    if (conditionData?.condition === "between" || conditionData?.condition === "notBetween") {
        return (
            <Box mt={2}>
                <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
                    {displayLabel}
                </Typography>
                <Flex alignItems={"flex-start"} gap={1}>
                    <Box flex={1}>
                        {renderInputField(
                            "fromValue",
                            conditionData?.fromValue,
                            val => updateConditionData({ fromValue: val }),
                            validationErrors.fromValue
                        )}
                        {validationErrors.fromValue && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                                {validationErrors.fromValue}
                            </Text>
                        )}
                    </Box>
                    <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small" mt={2}>
                        {t("and")}
                    </Typography>
                    <Box flex={1}>
                        {renderInputField(
                            "toValue",
                            conditionData?.toValue,
                            val => updateConditionData({ toValue: val }),
                            validationErrors.toValue,
                            false
                        )}
                        {validationErrors.toValue && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                                {validationErrors.toValue}
                            </Text>
                        )}
                    </Box>
                </Flex>
            </Box>
        );
    } else {
        return (
            <Box mt={2}>
                <Flex gap={1} flexDirection={"column"}>
                    <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
                        {displayLabel}
                    </Typography>
                    <Box>
                        {renderInputField(
                            "value",
                            conditionData?.value,
                            val => updateConditionData({ value: val }),
                            validationErrors.value
                        )}
                        {validationErrors.value && <FieldError error={validationErrors.value} />}
                    </Box>
                </Flex>
            </Box>
        );
    }
};

export default DaySelector;